import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, MenuItem } from '@mui/material';

import { useGetDepartments } from '../../hooks/departments/useGetDepartments';
import { useDeleteDepartment } from '../../hooks/departments/useDeleteDepartment';
import { useDeleteAllDepartments } from '../../hooks/departments/useDeleteAllDepartments';
import { useForceDeleteDepartment } from '../../hooks/departments/useForceDeleteDepartment';
import { useForceDeleteAllDepartments } from '../../hooks/departments/useForceDeleteAllDepartments';
import { useRestoreDepartment } from '../../hooks/departments/useRestoreDepartment';
import { useRestoreAllDepartments } from '../../hooks/departments/useRestoreAllDepartments';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { usePermissions } from '../../auth/PermissionsContext';

import useTableActions from '../../hooks/common/useTableActions';
import TableLayout from '../global/Table/TableLayout';
import ArchiveButton from '../global/Table/ArchiveButton';
import DepartmentsTableColumns from './Table/DepartmentsTableColumns';
import DepartmentsTableRow from './Table/DepartmentsTableRow';

const DepartmentsTable = ({ isArchive = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const navigate = useNavigate();
    const { has } = usePermissions();

    const [filterSchoolId, setFilterSchoolId] = useState('');

    const perms = useMemo(() => ({
        list: has('departments_list'),
        create: has('departments_create'),
        edit: has('departments_edit'),
        delete: has('departments_delete'),
        archive_list: has('departments_archive_list'),
        restore: has('departments_restore'),
        force_delete: has('departments_force_delete'),
    }), [has]);

    const { deleteDepartment, isDelete } = useDeleteDepartment();
    const { deleteAllDepartments, isDelete: isDeletingAll } = useDeleteAllDepartments();
    const { forceDeleteDepartment } = useForceDeleteDepartment();
    const { forceDeleteAllDepartments } = useForceDeleteAllDepartments();
    const { restoreDepartment } = useRestoreDepartment();
    const { restoreAllDepartments } = useRestoreAllDepartments();

    const { schools } = useGetSchools(1, 999);

    const {
        page, perPage, search, sortBy, sortDir, selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/departments',
        deleteSingle: deleteDepartment,
        deleteAll: deleteAllDepartments,
        forceDeleteSingle: forceDeleteDepartment,
        forceDeleteAll: forceDeleteAllDepartments,
        restoreSingle: restoreDepartment,
        restoreAll: restoreAllDepartments,
        isArchive,
    });

    const filters = useMemo(() => ({
        search: search || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortDir || undefined,
        school_id: filterSchoolId || undefined,
        ...(isArchive ? { only_trashed: 1 } : {}),
    }), [search, sortBy, sortDir, filterSchoolId, isArchive]);

    const { isLoading, error, departments, meta } = useGetDepartments(page, perPage, filters);

    const renderRow = (params) => (
        <DepartmentsTableRow
            key={params.key || params.item.id}
            item={params.item}
            index={params.index}
            isArabic={params.isArabic}
            isDark={params.isDark}
            page={params.page}
            perPage={params.perPage}
            order={params.order}
            totalItems={departments.length}
            selectedIds={params.selectedIds}
            onRowCheckboxClick={params.onRowCheckboxClick}
            onMenuOpen={params.onMenuOpen}
        />
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    select
                    size="small"
                    value={filterSchoolId}
                    onChange={(e) => setFilterSchoolId(e.target.value)}
                    label={isArabic ? 'فلتر حسب المدرسة' : 'Filter by School'}
                    dir={isArabic ? 'rtl' : 'ltr'}
                    sx={{
                        minWidth: 220,
                        ...(isArabic && {
                            '& .MuiInputLabel-root': {
                                left: 'unset',
                                right: 32,
                                transformOrigin: 'top right',
                            },
                            '& .MuiInputLabel-shrink': {
                                right: 32,
                            },
                            '& .MuiSelect-icon': { left: 7, right: 'unset' },
                            '& .MuiSelect-select': {
                                textAlign: 'right',
                                paddingRight: '14px !important',
                                paddingLeft: '32px !important',
                            },
                            '& .MuiOutlinedInput-notchedOutline legend': {
                                textAlign: 'right',
                            },
                        }),
                    }}
                    SelectProps={{
                        MenuProps: {
                            PaperProps: {
                                sx: { direction: isArabic ? 'rtl' : 'ltr' },
                            },
                        },
                    }}
                >
                    <MenuItem value="">
                        {isArabic ? 'الكل' : 'All'}
                    </MenuItem>
                    {(schools || []).map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.name}
                        </MenuItem>
                    ))}
                </TextField>

                <Box sx={{ marginInlineStart: 'auto' }}>
                    {!isArchive && perms.archive_list && (
                        <ArchiveButton to="/departments/archive" />
                    )}
                    {isArchive && perms.list && (
                        <ArchiveButton
                            to="/departments"
                            archiveLabelAr="العودة للقائمة"
                            archiveLabelEn="Back to List"
                        />
                    )}
                </Box>
            </Box>

            <TableLayout
                title={isArabic
                    ? (isArchive ? 'أرشيف الأقسام' : 'الأقسام')
                    : (isArchive ? 'Departments Archive' : 'Departments')}
                subtitle={isArabic
                    ? (isArchive ? 'إدارة الأقسام المحذوفة' : 'إدارة الأقسام في النظام')
                    : (isArchive ? 'Manage deleted departments' : 'Manage departments')}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إضافة قسم' : 'Add Department'}
                onAddClick={() => navigate('/departments/create')}
                addButtonVisible={!isArchive && perms.create}
                isLoading={isLoading}
                error={error}
                items={departments}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, departments)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                onBulkRestore={isArchive && perms.restore ? handleBulkRestore : undefined}
                isBulkDeleting={isDeletingAll}
                columns={DepartmentsTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuEdit={!isArchive && perms.edit ? handleEdit : undefined}
                onMenuDelete={isArchive
                    ? (perms.force_delete ? handleMenuDelete : undefined)
                    : (perms.delete ? handleMenuDelete : undefined)}
                onMenuRestore={isArchive && perms.restore ? handleMenuRestore : undefined}
                menuDisableEdit={isArchive || !perms.edit}
                menuDisableDelete={isArchive ? !perms.force_delete : !perms.delete}
                menuDisableRestore={!isArchive || !perms.restore}
                isDeletingSingle={isDelete}
                emptyLabelAr={isArchive ? 'لا توجد أقسام في الأرشيف' : 'لا توجد أقسام'}
                emptyLabelEn={isArchive ? 'No archived departments' : 'No departments'}
                loadErrorAr="حدث خطأ أثناء تحميل الأقسام"
                loadErrorEn="Failed to load departments"
            />
        </>
    );
};

export default DepartmentsTable;
